import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';

import { ChatDailyReportApiService } from '../api/chat/chat-daily-report.api.service';
import { UserTokenHeavy } from '../types/User';
import { DailyReport, DailyReportResult, DailyReportState } from '../types/Chatbot';
import { LocalStorageService } from './local-storage.service';
import { AuthenticationService } from './authentication.service';

const STORAGE_KEY_CACHE = 'vev-iq-daily-report-cache';
const STORAGE_KEY_DISMISSED = 'vev-iq-daily-report-dismissed';

@Injectable({
  providedIn: 'root',
})
export class ChatDailyReportService {
  private readonly _state = signal<DailyReportState>({
    result: null,
    isLoading: false,
    error: false,
    isDismissed: false,
  });

  public readonly state = this._state.asReadonly();

  private initialized = false;
  private lastInitializedDate: string | null = null;
  private lastInitializedScope: string | null = null;

  public constructor(
    private readonly apiService: ChatDailyReportApiService,
    private readonly localStorageService: LocalStorageService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  public get currentState(): DailyReportState {
    return this._state();
  }

  public initialize(): void {
    const today = this.todayString();
    const scope = this.storageScope();

    if (
      this.initialized &&
      this.lastInitializedDate === today &&
      this.lastInitializedScope === scope &&
      !this.currentState.error
    ) {
      return;
    }

    this.initialized = true;
    this.lastInitializedDate = today;
    this.lastInitializedScope = scope;
    this.loadFromCacheOrFetch();
  }

  // Fetches the merged report through the authenticated HTTP stack so the
  // bearer token is attached; the endpoint then redirects to the stored file.
  public downloadReport(url: string): Observable<Blob> {
    return this.apiService.downloadReport(url);
  }

  public dismiss(): void {
    this.localStorageService.setItem(this.storageKey(STORAGE_KEY_DISMISSED), this.todayString());
    this.updateState({ isDismissed: true });
  }

  public restore(): void {
    this.localStorageService.removeItem(this.storageKey(STORAGE_KEY_DISMISSED));
    this.updateState({ isDismissed: false });
  }

  private loadFromCacheOrFetch(): void {
    const today = this.todayString();

    const dismissedDate = this.localStorageService.getItem(this.storageKey(STORAGE_KEY_DISMISSED));
    const isDismissed = dismissedDate === today;

    const cachedRaw = this.localStorageService.getItem(this.storageKey(STORAGE_KEY_CACHE));
    if (cachedRaw) {
      try {
        const cached: { date: string; report: DailyReport } = JSON.parse(cachedRaw);
        if (cached.date === today && cached.report) {
          this.updateState({
            result: { kind: 'merged', report: cached.report },
            isLoading: false,
            error: false,
            isDismissed,
          });
          return;
        }
      } catch {
        // ignore corrupt cache, fall through to fetch
      }
    }

    this.updateState({ result: null, isLoading: true, error: false, isDismissed });
    this.apiService.getLatestReport().subscribe({
      next: (result) => {
        this.cacheResult(today, result);
        this.updateState({ result, isLoading: false, error: false });
      },
      error: () => {
        this.updateState({ isLoading: false, error: true });
      },
    });
  }

  // Only the merged JSON shape is cacheable; the ZIP blob doesn't fit in
  // localStorage, so site admins refetch once per session instead.
  private cacheResult(today: string, result: DailyReportResult): void {
    if (result.kind !== 'merged') {
      return;
    }
    this.localStorageService.setItem(
      this.storageKey(STORAGE_KEY_CACHE),
      JSON.stringify({ date: today, report: result.report }),
    );
  }

  private todayString(): string {
    const now = new Date();
    // Report is generated at 6am UTC, so before 6am the active report is still yesterday's
    if (now.getUTCHours() < 6) {
      now.setUTCDate(now.getUTCDate() - 1);
    }
    return now.toISOString().slice(0, 10);
  }

  private storageKey(baseKey: string): string {
    return `${baseKey}:${this.storageScope()}`;
  }

  private storageScope(): string {
    const user = this.authenticationService.getLoggedUser();
    return this.userScope(user);
  }

  private userScope(user: UserTokenHeavy | null | undefined): string {
    const tenantId = user?.tenantID ?? 'anonymous-tenant';
    const userId = user?.id ?? user?.email ?? 'anonymous-user';
    return `${tenantId}:${userId}`;
  }

  private updateState(partial: Partial<DailyReportState>): void {
    this._state.update((s) => ({ ...s, ...partial }));
  }
}
